// TaskWizardModal.tsx

import { useQuery } from '@tanstack/react-query';
import { PageHead } from 'ui/kit/PageHead';
import type { FormikProps } from 'formik';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { FormContainer } from 'components/formsV2/formContainer';
import { isAuthSessionActive, useAuthSession } from 'hooks/useAuthSession';
import { fetchProfileByType } from 'modules/Profile/model/services';
import { Button, Modal } from 'ui';
import { studyTimeError, studyTimeToApi, type StudyTime } from 'utils/studyTime';

import { taskValidationSchema } from './comfig';
import { renderStep } from './renderField';
import { getVisibleSteps } from './taskForm.steps';
import { useTaskFormStore } from './taskForm.store';
import styles from './TaskWizardModal.module.scss';

export const TaskWizardModal = ({
  open,
  onClose,
  initialStep = 0,
  title = 'Новая задача',
  inline = false,
  onFinish,
}: {
  open: boolean;
  onClose: () => void;
  initialStep?: number;
  /**
   * Мастер как сама страница (карточка в колонке, Figma 55:35441), без модалки.
   * Используется для новой задачи на /profile/details/task.
   */
  inline?: boolean;
  /** Вызывается после последнего шага вместо перехода или закрытия по умолчанию. */
  onFinish?: () => void;
  /** Заголовок мастера: «Новая задача» при создании, «Редактирование задачи» при правке. */
  title?: string;
}) => {
  const { values: storedValues, setValues } = useTaskFormStore();
  const { data: session } = useAuthSession();
  const { data: profile, isPending: profilePending, refetch: refetchStudentProfile } = useQuery({
    queryKey: ['profile', 'student'],
    queryFn: () => fetchProfileByType('student'),
    enabled: open && isAuthSessionActive(session),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  });

  useEffect(() => {
    if (open && isAuthSessionActive(session)) {
      void refetchStudentProfile();
    }
  }, [open, session, refetchStudentProfile]);

  const [currentStep, setCurrentStep] = useState(initialStep);
  const location = useLocation();
  const navigate = useNavigate();
  const isCreateFlow = location.pathname === '/profile/tasks';

  const profileContext = useMemo(
    () => ({
      registrant_type: (profile?.registrant_type as 'self' | 'parent' | undefined) || 'self',
      children: (profile?.children as Array<Record<string, string>>) || [],
      name: profile?.name,
      surname: profile?.surname,
    }),
    [profile],
  );

  // Persist may keep studentType='school' from a previous draft, which inserts
  // the "Класс обучения" step. For self (adult) on a fresh create, default to graduated.
  const didInitSelfTypeRef = useRef(false);

  useEffect(() => {
    if (!open) {
      didInitSelfTypeRef.current = false;
      return;
    }
    if (didInitSelfTypeRef.current) return;
    if (profilePending) return;

    const registrantType =
      (profile?.registrant_type as 'self' | 'parent' | undefined) || 'self';
    if (registrantType !== 'self') {
      didInitSelfTypeRef.current = true;
      return;
    }

    didInitSelfTypeRef.current = true;
    const current = useTaskFormStore.getState().values;
    const currentType = String(current.studentType || '');
    if (!currentType || isCreateFlow) {
      if (currentType !== 'graduated') {
        setValues({
          ...current,
          studentType: 'graduated',
          grades: [],
          isSelf: true,
        });
      }
    }
  }, [open, profile, profilePending, isCreateFlow, setValues]);

  const wizard = (
      <div className={`${styles.FormClass} ${inline ? styles.inlineCard : ''}`}>
        <FormContainer
          initialValues={storedValues}
          validationSchema={taskValidationSchema}
          enableReinitialize
          onSubmit={() => {}}
        >
          {({
            values,
            errors,
            touched,
            setFieldTouched,
            setFieldValue,
            validateForm,
          }: FormikProps<Record<string, unknown>>) => {
            const steps = getVisibleSteps(values, profileContext);

            useEffect(() => {
              const isSelf = profileContext.registrant_type !== 'parent';
              setFieldValue('isSelf', isSelf);
              // p5: имени ребёнка нет - в заявку идёт имя аккаунта (как до p1); родителю без фамилии.
              const fullName = [profileContext.name, isSelf ? profileContext.surname : ''].filter(Boolean).join(' ');
              if (fullName) setFieldValue('studentName', fullName);
              if (!values.teacherGender) setFieldValue('teacherGender', 'any');
            }, [profileContext.registrant_type, profileContext.name, profileContext.surname, profileContext.children]);

            useEffect(() => {
              if (currentStep >= steps.length) {
                setCurrentStep(Math.max(steps.length - 1, 0));
              }
            }, [steps.length, currentStep]);

            const step = steps[currentStep];

            const handleNextStep = async () => {
              const formErrors = await validateForm();
              const currentFields = step?.fields || [];
              const extraErrors: Record<string, string> = {};

              if (step?.key === 'learnerType' && !values.studentType) {
                extraErrors.studentType = 'Выберите, кто будет заниматься';
              }

              const mergedErrors = { ...formErrors, ...extraErrors };
              const hasError = currentFields.some((field) => mergedErrors[field]);

              if (hasError) {
                currentFields.forEach((field) => setFieldTouched(field, true));
                return;
              }

              setValues(values);

              if (currentStep < steps.length - 1) {
                setCurrentStep((prev) => prev + 1);
              } else {
                setValues(values);
                if (onFinish) {
                  onFinish();
                } else if (isCreateFlow) {
                  navigate('/profile/details/task');
                } else {
                  onClose();
                }
              }
            };

            if (!step) return null;

            return (
              <div className={styles.modalPanel}>
                <div className={styles.modalInner}>
                  {!inline ? (
                    <>
                      <p className={styles.crumb}>Личный кабинет / Мои задачи</p>
                      <h2 className={styles.pageTitle}>{title}</h2>
                    </>
                  ) : null}
                  <p className={styles.stepMeta}>
                    Шаг {currentStep + 1} из{' '}
                    {/* На первом шаге считаем и «Класс» - иначе выбор «Школьник» менял «1 из 9» на «1 из 10». */}
                    {currentStep === 0
                      ? getVisibleSteps({ ...values, studentType: 'school' }, profileContext).length
                      : steps.length}
                  </p>
                  <h3 className={styles.modalTitle}>{step.title}</h3>

                  {renderStep(
                    step.key,
                    values,
                    setFieldValue,
                    errors,
                    touched,
                    profileContext,
                  )}
                </div>
                <div className={styles.modalActions}>
                  <Button
                    variant='filled'
                    color='secondary'
                    shape='brick'
                    size='md'
                    type='button'
                    onClick={() =>
                      currentStep > 0 ? setCurrentStep((s) => s - 1) : onClose()
                    }
                  >
                    Назад
                  </Button>

                  <Button
                    variant='filled'
                    color='primary'
                    shape='brick'
                    size='md'
                    type='button'
                    onClick={handleNextStep}
                  >
                    {(step.key === 'comment' && !values.comment) ||
                    (step.key === 'schedule' &&
                      !studyTimeError(values.studyTime as StudyTime | null, false) &&
                      !studyTimeToApi(values.studyTime as StudyTime | null))
                      ? 'Пропустить'
                      : 'Продолжить'}
                  </Button>
                </div>
              </div>
            );
          }}
        </FormContainer>
      </div>
  );

  if (inline) {
    // pagehead-2026-10-09: на странице кабинета шапка общая (PageHead), карточка мастера ниже.
    return open ? (
      <>
        <PageHead crumb='Личный кабинет / Мои задачи' title={title} />
        {wizard}
      </>
    ) : null;
  }

  return (
    <Modal widthModal={488} heightModal={700} open={open} onClose={onClose}>
      {wizard}
    </Modal>
  );
};